import React, { useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard, Package, Users, Truck, History, Map, BarChart3,
  Settings, LogOut, Menu, X, PlusCircle, Building2,
} from "lucide-react";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true, roles: ["admin", "dispatch"] },
  { to: "/admin/orders", label: "Orders", icon: Package, roles: ["admin", "dispatch"] },
  { to: "/admin/map", label: "Live Map", icon: Map, roles: ["admin", "dispatch"] },
  { to: "/admin/history", label: "History", icon: History, roles: ["admin", "dispatch"] },
  { to: "/admin/customers", label: "Customers", icon: Building2, roles: ["admin", "dispatch"] },
  { to: "/admin/drivers", label: "Drivers", icon: Users, roles: ["admin"] },
  { to: "/admin/reports", label: "Reports", icon: BarChart3, roles: ["admin", "dispatch"] },
  { to: "/admin/settings", label: "Settings", icon: Settings, roles: ["admin"] },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const items = NAV.filter((n) => n.roles.includes(user?.role));

  const doLogout = async () => { await logout(); navigate("/login"); };

  const SidebarInner = () => (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-2.5 px-5 py-5">
        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#FF6500] text-white"><Truck size={20} /></div>
        <div>
          <div className="font-head text-base font-extrabold leading-none text-white">GS Pacific</div>
          <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">ePOD Command</div>
        </div>
      </div>
      <nav className="flex-1 space-y-1 px-3 py-2">
        {items.map((n) => (
          <NavLink
            key={n.to} to={n.to} end={n.end} onClick={() => setOpen(false)}
            data-testid={`nav-${n.label.replace(/\s+/g, "-").toLowerCase()}`}
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                isActive ? "bg-[#1E3E62] text-white" : "text-slate-300 hover:bg-white/5 hover:text-white"
              }`
            }
          >
            <n.icon size={18} /> {n.label}
          </NavLink>
        ))}
      </nav>
      <div className="border-t border-white/10 p-3">
        <div className="mb-2 flex items-center gap-3 px-2">
          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#1E3E62] text-sm font-bold text-white">
            {user?.name?.[0]}
          </div>
          <div className="min-w-0">
            <div className="truncate text-sm font-semibold text-white">{user?.name}</div>
            <div className="text-xs capitalize text-slate-400">{user?.role}</div>
          </div>
        </div>
        <button onClick={doLogout} data-testid="logout-button" className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-slate-300 hover:bg-white/5">
          <LogOut size={16} /> Sign out
        </button>
      </div>
    </div>
  );

  return (
    <div className="flex min-h-screen bg-[#F8FAFC]">
      {/* Desktop sidebar */}
      <aside className="hidden w-64 shrink-0 bg-[#0B192C] lg:block"><SidebarInner /></aside>

      {/* Mobile drawer */}
      {open && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} />
          <aside className="absolute left-0 top-0 h-full w-64 bg-[#0B192C]"><SidebarInner /></aside>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex items-center justify-between border-b border-slate-200 bg-white/90 px-4 py-3 backdrop-blur lg:px-8">
          <div className="flex items-center gap-3">
            <button className="lg:hidden" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
            <div className="text-sm text-slate-500">
              <span className="font-semibold text-[#0B192C]">Eastern Creek DC #4</span> · Sydney NSW
            </div>
          </div>
          <button
            onClick={() => navigate("/admin/orders/new")}
            data-testid="header-create-order-btn"
            className="flex items-center gap-2 rounded-xl bg-[#FF6500] px-4 py-2 text-sm font-bold text-white tap-shrink hover:bg-[#E05A00]"
          >
            <PlusCircle size={18} /> <span className="hidden sm:inline">Create Order</span>
          </button>
        </header>
        <main className="min-w-0 flex-1 p-4 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
